import { Tool } from "@/types";
import { Locale } from "@/lib/i18n";

type LocaleToolPack = {
  descriptionTemplate: string;
  converterTemplate: string;
  categories: Record<string, string>;
  mergePdf: string;
  splitPdf: string;
  compressPdf: string;
  rotatePdf: string;
  addWatermark: string;
  editPdf: string;
  signPdf: string;
  imageResizer: string;
  imageCompressor: string;
  imageCropper: string;
  imageConverter: string;
  removeBackground: string;
  imageToVector: string;
  wordCounter: string;
  caseConverter: string;
  loremIpsum: string;
  jsonFormatter: string;
  diffChecker: string;
  markdownEditor: string;
  qrGenerator: string;
  passwordGenerator: string;
  base64Encoder: string;
  base64Decoder: string;
  colorPicker: string;
  urlEncoder: string;
  urlDecoder: string;
  uuidGenerator: string;
};

const toolLocalePacks: Record<Locale, LocaleToolPack> = {
  en: {
    descriptionTemplate: "Use {name} online with a fast, secure workflow.",
    converterTemplate: "{from} to {to} Converter",
    categories: { PDF: "PDF", Image: "Image", Text: "Text", Dev: "Dev", Other: "Other" },
    mergePdf: "Merge PDF Files Online",
    splitPdf: "Split PDF Pages Online",
    compressPdf: "Compress PDF File Size",
    rotatePdf: "Rotate PDF Pages Online",
    addWatermark: "Add Watermark to PDF",
    editPdf: "Edit PDF Online Free",
    signPdf: "Sign PDF Electronically",
    imageResizer: "Image Resizer Online Free",
    imageCompressor: "Image Compressor Online",
    imageCropper: "Crop Image Online Free",
    imageConverter: "Image Format Converter",
    removeBackground: "Remove Background from Image",
    imageToVector: "Image to Vector (SVG)",
    wordCounter: "Word Counter",
    caseConverter: "Case Converter",
    loremIpsum: "Lorem Ipsum Generator",
    jsonFormatter: "JSON Formatter",
    diffChecker: "Diff Checker",
    markdownEditor: "Markdown Editor",
    qrGenerator: "QR Code Generator",
    passwordGenerator: "Password Generator",
    base64Encoder: "Base64 Encoder",
    base64Decoder: "Base64 Decoder",
    colorPicker: "Color Picker",
    urlEncoder: "URL Encoder",
    urlDecoder: "URL Decoder",
    uuidGenerator: "UUID Generator",
  },
  id: {
    descriptionTemplate: "Gunakan {name} secara online dengan workflow yang cepat dan aman.",
    converterTemplate: "Konverter {from} ke {to}",
    categories: { PDF: "PDF", Image: "Gambar", Text: "Teks", Dev: "Dev", Other: "Lainnya" },
    mergePdf: "Gabungkan File PDF",
    splitPdf: "Pisahkan Halaman PDF",
    compressPdf: "Kompres Ukuran PDF",
    rotatePdf: "Putar Halaman PDF",
    addWatermark: "Tambahkan Watermark ke PDF",
    editPdf: "Edit PDF Online Gratis",
    signPdf: "Tanda Tangan PDF Elektronik",
    imageResizer: "Resize Gambar Online",
    imageCompressor: "Kompres Gambar Online",
    imageCropper: "Crop Gambar Online",
    imageConverter: "Konverter Format Gambar",
    removeBackground: "Hapus Background Gambar",
    imageToVector: "Gambar ke Vektor (SVG)",
    wordCounter: "Penghitung Kata",
    caseConverter: "Konverter Huruf",
    loremIpsum: "Generator Lorem Ipsum",
    jsonFormatter: "Formatter JSON",
    diffChecker: "Pengecek Perbedaan",
    markdownEditor: "Editor Markdown",
    qrGenerator: "Generator Kode QR",
    passwordGenerator: "Generator Password",
    base64Encoder: "Encoder Base64",
    base64Decoder: "Decoder Base64",
    colorPicker: "Pemilih Warna",
    urlEncoder: "Encoder URL",
    urlDecoder: "Decoder URL",
    uuidGenerator: "Generator UUID",
  },
  vi: {
    descriptionTemplate: "Sử dụng {name} trực tuyến với quy trình nhanh và an toàn.",
    converterTemplate: "Trình chuyển đổi {from} sang {to}",
    categories: { PDF: "PDF", Image: "Hình ảnh", Text: "Văn bản", Dev: "Dev", Other: "Khác" },
    mergePdf: "Gộp tệp PDF",
    splitPdf: "Tách trang PDF",
    compressPdf: "Nén dung lượng PDF",
    rotatePdf: "Xoay trang PDF",
    addWatermark: "Thêm watermark vào PDF",
    editPdf: "Chỉnh sửa PDF trực tuyến",
    signPdf: "Ký PDF điện tử",
    imageResizer: "Thay đổi kích thước ảnh",
    imageCompressor: "Nén ảnh trực tuyến",
    imageCropper: "Cắt ảnh trực tuyến",
    imageConverter: "Trình chuyển đổi định dạng ảnh",
    removeBackground: "Xóa nền ảnh",
    imageToVector: "Ảnh sang vector (SVG)",
    wordCounter: "Đếm từ",
    caseConverter: "Chuyển đổi kiểu chữ",
    loremIpsum: "Trình tạo Lorem Ipsum",
    jsonFormatter: "Định dạng JSON",
    diffChecker: "Kiểm tra khác biệt",
    markdownEditor: "Trình soạn Markdown",
    qrGenerator: "Trình tạo mã QR",
    passwordGenerator: "Trình tạo mật khẩu",
    base64Encoder: "Mã hóa Base64",
    base64Decoder: "Giải mã Base64",
    colorPicker: "Bộ chọn màu",
    urlEncoder: "Mã hóa URL",
    urlDecoder: "Giải mã URL",
    uuidGenerator: "Trình tạo UUID",
  },
  cs: {
    descriptionTemplate: "Použijte nástroj {name} online v rychlém a bezpečném workflow.",
    converterTemplate: "Převodník {from} na {to}",
    categories: { PDF: "PDF", Image: "Obrázek", Text: "Text", Dev: "Dev", Other: "Ostatní" },
    mergePdf: "Sloučit PDF soubory",
    splitPdf: "Rozdělit PDF stránky",
    compressPdf: "Komprese PDF",
    rotatePdf: "Otočit PDF stránky",
    addWatermark: "Přidat vodoznak do PDF",
    editPdf: "Upravit PDF online",
    signPdf: "Elektronicky podepsat PDF",
    imageResizer: "Změna velikosti obrázku",
    imageCompressor: "Komprese obrázků",
    imageCropper: "Oříznout obrázek online",
    imageConverter: "Převodník formátu obrázků",
    removeBackground: "Odstranit pozadí z obrázku",
    imageToVector: "Obrázek do vektoru (SVG)",
    wordCounter: "Počítadlo slov",
    caseConverter: "Převod velikosti písmen",
    loremIpsum: "Generátor Lorem Ipsum",
    jsonFormatter: "Formátovač JSON",
    diffChecker: "Porovnání rozdílů",
    markdownEditor: "Editor Markdown",
    qrGenerator: "Generátor QR kódů",
    passwordGenerator: "Generátor hesel",
    base64Encoder: "Kodér Base64",
    base64Decoder: "Dekodér Base64",
    colorPicker: "Výběr barvy",
    urlEncoder: "Kodér URL",
    urlDecoder: "Dekodér URL",
    uuidGenerator: "Generátor UUID",
  },
  es: {
    descriptionTemplate: "Usa {name} en línea con un flujo rápido y seguro.",
    converterTemplate: "Convertidor de {from} a {to}",
    categories: { PDF: "PDF", Image: "Imagen", Text: "Texto", Dev: "Dev", Other: "Otros" },
    mergePdf: "Unir archivos PDF",
    splitPdf: "Dividir páginas PDF",
    compressPdf: "Comprimir PDF",
    rotatePdf: "Rotar páginas PDF",
    addWatermark: "Agregar marca de agua a PDF",
    editPdf: "Editar PDF en línea",
    signPdf: "Firmar PDF electrónicamente",
    imageResizer: "Redimensionador de imágenes",
    imageCompressor: "Compresor de imágenes",
    imageCropper: "Recortar imagen en línea",
    imageConverter: "Convertidor de formatos de imagen",
    removeBackground: "Eliminar fondo de imagen",
    imageToVector: "Imagen a vector (SVG)",
    wordCounter: "Contador de palabras",
    caseConverter: "Conversor de mayúsculas y minúsculas",
    loremIpsum: "Generador Lorem Ipsum",
    jsonFormatter: "Formateador JSON",
    diffChecker: "Comparador de diferencias",
    markdownEditor: "Editor Markdown",
    qrGenerator: "Generador de códigos QR",
    passwordGenerator: "Generador de contraseñas",
    base64Encoder: "Codificador Base64",
    base64Decoder: "Decodificador Base64",
    colorPicker: "Selector de color",
    urlEncoder: "Codificador URL",
    urlDecoder: "Decodificador URL",
    uuidGenerator: "Generador UUID",
  },
  fr: {
    descriptionTemplate: "Utilisez {name} en ligne avec un workflow rapide et sécurisé.",
    converterTemplate: "Convertisseur {from} vers {to}",
    categories: { PDF: "PDF", Image: "Image", Text: "Texte", Dev: "Dev", Other: "Autres" },
    mergePdf: "Fusionner des fichiers PDF",
    splitPdf: "Séparer des pages PDF",
    compressPdf: "Compresser un PDF",
    rotatePdf: "Faire pivoter les pages PDF",
    addWatermark: "Ajouter un filigrane à un PDF",
    editPdf: "Modifier un PDF en ligne",
    signPdf: "Signer un PDF électroniquement",
    imageResizer: "Redimensionneur d'images",
    imageCompressor: "Compresseur d'images",
    imageCropper: "Rogner une image en ligne",
    imageConverter: "Convertisseur de formats d'image",
    removeBackground: "Supprimer l'arrière-plan d'une image",
    imageToVector: "Image en vecteur (SVG)",
    wordCounter: "Compteur de mots",
    caseConverter: "Convertisseur de casse",
    loremIpsum: "Générateur Lorem Ipsum",
    jsonFormatter: "Formateur JSON",
    diffChecker: "Vérificateur de différences",
    markdownEditor: "Éditeur Markdown",
    qrGenerator: "Générateur de QR codes",
    passwordGenerator: "Générateur de mots de passe",
    base64Encoder: "Encodeur Base64",
    base64Decoder: "Décodeur Base64",
    colorPicker: "Sélecteur de couleur",
    urlEncoder: "Encodeur d'URL",
    urlDecoder: "Décodeur d'URL",
    uuidGenerator: "Générateur UUID",
  },
  de: {
    descriptionTemplate: "Verwende {name} online mit einem schnellen und sicheren Workflow.",
    converterTemplate: "{from}-zu-{to}-Konverter",
    categories: { PDF: "PDF", Image: "Bild", Text: "Text", Dev: "Dev", Other: "Andere" },
    mergePdf: "PDF-Dateien zusammenführen",
    splitPdf: "PDF-Seiten trennen",
    compressPdf: "PDF-Dateigröße komprimieren",
    rotatePdf: "PDF-Seiten drehen",
    addWatermark: "Wasserzeichen zu PDF hinzufügen",
    editPdf: "PDF online bearbeiten",
    signPdf: "PDF elektronisch signieren",
    imageResizer: "Bildgrößenänderung online",
    imageCompressor: "Bildkomprimierung online",
    imageCropper: "Bild online zuschneiden",
    imageConverter: "Bildformat-Konverter",
    removeBackground: "Bildhintergrund entfernen",
    imageToVector: "Bild zu Vektor (SVG)",
    wordCounter: "Wortzähler",
    caseConverter: "Groß-/Kleinschreibung-Konverter",
    loremIpsum: "Lorem-Ipsum-Generator",
    jsonFormatter: "JSON-Formatter",
    diffChecker: "Diff-Prüfer",
    markdownEditor: "Markdown-Editor",
    qrGenerator: "QR-Code-Generator",
    passwordGenerator: "Passwort-Generator",
    base64Encoder: "Base64-Encoder",
    base64Decoder: "Base64-Decoder",
    colorPicker: "Farbwähler",
    urlEncoder: "URL-Encoder",
    urlDecoder: "URL-Decoder",
    uuidGenerator: "UUID-Generator",
  },
  it: {
    descriptionTemplate: "Usa {name} online con un flusso rapido e sicuro.",
    converterTemplate: "Convertitore da {from} a {to}",
    categories: { PDF: "PDF", Image: "Immagine", Text: "Testo", Dev: "Dev", Other: "Altro" },
    mergePdf: "Unisci file PDF",
    splitPdf: "Dividi pagine PDF",
    compressPdf: "Comprimi PDF",
    rotatePdf: "Ruota pagine PDF",
    addWatermark: "Aggiungi filigrana al PDF",
    editPdf: "Modifica PDF online",
    signPdf: "Firma PDF elettronicamente",
    imageResizer: "Ridimensiona immagini online",
    imageCompressor: "Compressore di immagini",
    imageCropper: "Ritaglia immagine online",
    imageConverter: "Convertitore di formato immagine",
    removeBackground: "Rimuovi sfondo dall'immagine",
    imageToVector: "Immagine in vettore (SVG)",
    wordCounter: "Contatore parole",
    caseConverter: "Convertitore maiuscole/minuscole",
    loremIpsum: "Generatore Lorem Ipsum",
    jsonFormatter: "Formattatore JSON",
    diffChecker: "Controllo differenze",
    markdownEditor: "Editor Markdown",
    qrGenerator: "Generatore di codici QR",
    passwordGenerator: "Generatore di password",
    base64Encoder: "Codificatore Base64",
    base64Decoder: "Decodificatore Base64",
    colorPicker: "Selettore colore",
    urlEncoder: "Codificatore URL",
    urlDecoder: "Decodificatore URL",
    uuidGenerator: "Generatore UUID",
  },
  hu: {
    descriptionTemplate: "Használd a(z) {name} eszközt online gyors és biztonságos munkafolyamattal.",
    converterTemplate: "{from}–{to} konverter",
    categories: { PDF: "PDF", Image: "Kép", Text: "Szöveg", Dev: "Fejlesztői", Other: "Egyéb" },
    mergePdf: "PDF fájlok egyesítése",
    splitPdf: "PDF oldalak szétválasztása",
    compressPdf: "PDF tömörítése",
    rotatePdf: "PDF oldalak forgatása",
    addWatermark: "Vízjel hozzáadása PDF-hez",
    editPdf: "PDF szerkesztése online",
    signPdf: "PDF elektronikus aláírása",
    imageResizer: "Képátméretező online",
    imageCompressor: "Képtömörítő online",
    imageCropper: "Képvágás online",
    imageConverter: "Képformátum-konverter",
    removeBackground: "Háttér eltávolítása képről",
    imageToVector: "Kép vektorrá (SVG)",
    wordCounter: "Szószámláló",
    caseConverter: "Betűméret-konverter",
    loremIpsum: "Lorem Ipsum generátor",
    jsonFormatter: "JSON formázó",
    diffChecker: "Eltérésellenőrző",
    markdownEditor: "Markdown szerkesztő",
    qrGenerator: "QR-kód generátor",
    passwordGenerator: "Jelszógenerátor",
    base64Encoder: "Base64 kódoló",
    base64Decoder: "Base64 dekódoló",
    colorPicker: "Színválasztó",
    urlEncoder: "URL-kódoló",
    urlDecoder: "URL-dekódoló",
    uuidGenerator: "UUID generátor",
  },
  ms: {
    descriptionTemplate: "Gunakan {name} dalam talian dengan aliran kerja yang pantas dan selamat.",
    converterTemplate: "Penukar {from} ke {to}",
    categories: { PDF: "PDF", Image: "Imej", Text: "Teks", Dev: "Dev", Other: "Lain-lain" },
    mergePdf: "Gabung fail PDF",
    splitPdf: "Pisahkan halaman PDF",
    compressPdf: "Mampatkan PDF",
    rotatePdf: "Putar halaman PDF",
    addWatermark: "Tambah watermark pada PDF",
    editPdf: "Edit PDF dalam talian",
    signPdf: "Tandatangan PDF secara elektronik",
    imageResizer: "Ubah saiz imej dalam talian",
    imageCompressor: "Pemampat imej dalam talian",
    imageCropper: "Potong imej dalam talian",
    imageConverter: "Penukar format imej",
    removeBackground: "Buang latar belakang imej",
    imageToVector: "Imej ke vektor (SVG)",
    wordCounter: "Pengira perkataan",
    caseConverter: "Penukar huruf besar kecil",
    loremIpsum: "Penjana Lorem Ipsum",
    jsonFormatter: "Pemformat JSON",
    diffChecker: "Pemeriksa perbezaan",
    markdownEditor: "Editor Markdown",
    qrGenerator: "Penjana Kod QR",
    passwordGenerator: "Penjana kata laluan",
    base64Encoder: "Pengekod Base64",
    base64Decoder: "Penyahkod Base64",
    colorPicker: "Pemilih warna",
    urlEncoder: "Pengekod URL",
    urlDecoder: "Penyahkod URL",
    uuidGenerator: "Penjana UUID",
  },
  nl: {
    descriptionTemplate: "Gebruik {name} online met een snelle en veilige workflow.",
    converterTemplate: "{from} naar {to} converter",
    categories: { PDF: "PDF", Image: "Afbeelding", Text: "Tekst", Dev: "Dev", Other: "Overig" },
    mergePdf: "PDF-bestanden samenvoegen",
    splitPdf: "PDF-pagina's splitsen",
    compressPdf: "PDF comprimeren",
    rotatePdf: "PDF-pagina's draaien",
    addWatermark: "Watermerk aan PDF toevoegen",
    editPdf: "PDF online bewerken",
    signPdf: "PDF elektronisch ondertekenen",
    imageResizer: "Afbeelding verkleinen online",
    imageCompressor: "Afbeeldingscompressor online",
    imageCropper: "Afbeelding online bijsnijden",
    imageConverter: "Afbeeldingsformaat-converter",
    removeBackground: "Achtergrond van afbeelding verwijderen",
    imageToVector: "Afbeelding naar vector (SVG)",
    wordCounter: "Woordenteller",
    caseConverter: "Hoofdletter-converter",
    loremIpsum: "Lorem Ipsum-generator",
    jsonFormatter: "JSON-formatter",
    diffChecker: "Verschilcontrole",
    markdownEditor: "Markdown-editor",
    qrGenerator: "QR-codegenerator",
    passwordGenerator: "Wachtwoordgenerator",
    base64Encoder: "Base64-encoder",
    base64Decoder: "Base64-decoder",
    colorPicker: "Kleurkiezer",
    urlEncoder: "URL-encoder",
    urlDecoder: "URL-decoder",
    uuidGenerator: "UUID-generator",
  },
  pl: {
    descriptionTemplate: "Użyj {name} online w szybkim i bezpiecznym workflow.",
    converterTemplate: "Konwerter {from} na {to}",
    categories: { PDF: "PDF", Image: "Obraz", Text: "Tekst", Dev: "Dev", Other: "Inne" },
    mergePdf: "Scal pliki PDF",
    splitPdf: "Podziel strony PDF",
    compressPdf: "Kompresuj PDF",
    rotatePdf: "Obróć strony PDF",
    addWatermark: "Dodaj znak wodny do PDF",
    editPdf: "Edytuj PDF online",
    signPdf: "Podpisz PDF elektronicznie",
    imageResizer: "Zmiana rozmiaru obrazu online",
    imageCompressor: "Kompresor obrazów online",
    imageCropper: "Przytnij obraz online",
    imageConverter: "Konwerter formatu obrazów",
    removeBackground: "Usuń tło z obrazu",
    imageToVector: "Obraz do wektora (SVG)",
    wordCounter: "Licznik słów",
    caseConverter: "Konwerter wielkości liter",
    loremIpsum: "Generator Lorem Ipsum",
    jsonFormatter: "Formatter JSON",
    diffChecker: "Sprawdzanie różnic",
    markdownEditor: "Edytor Markdown",
    qrGenerator: "Generator kodów QR",
    passwordGenerator: "Generator haseł",
    base64Encoder: "Koder Base64",
    base64Decoder: "Dekoder Base64",
    colorPicker: "Selektor kolorów",
    urlEncoder: "Koder URL",
    urlDecoder: "Dekoder URL",
    uuidGenerator: "Generator UUID",
  },
  pt: {
    descriptionTemplate: "Use {name} online com um fluxo rápido e seguro.",
    converterTemplate: "Conversor de {from} para {to}",
    categories: { PDF: "PDF", Image: "Imagem", Text: "Texto", Dev: "Dev", Other: "Outros" },
    mergePdf: "Juntar ficheiros PDF",
    splitPdf: "Dividir páginas PDF",
    compressPdf: "Comprimir PDF",
    rotatePdf: "Rodar páginas PDF",
    addWatermark: "Adicionar marca de água ao PDF",
    editPdf: "Editar PDF online",
    signPdf: "Assinar PDF eletronicamente",
    imageResizer: "Redimensionar imagens online",
    imageCompressor: "Compressor de imagens online",
    imageCropper: "Cortar imagem online",
    imageConverter: "Conversor de formatos de imagem",
    removeBackground: "Remover fundo da imagem",
    imageToVector: "Imagem para vetor (SVG)",
    wordCounter: "Contador de palavras",
    caseConverter: "Conversor de maiúsculas e minúsculas",
    loremIpsum: "Gerador Lorem Ipsum",
    jsonFormatter: "Formatador JSON",
    diffChecker: "Verificador de diferenças",
    markdownEditor: "Editor Markdown",
    qrGenerator: "Gerador de códigos QR",
    passwordGenerator: "Gerador de palavras-passe",
    base64Encoder: "Codificador Base64",
    base64Decoder: "Descodificador Base64",
    colorPicker: "Seletor de cores",
    urlEncoder: "Codificador URL",
    urlDecoder: "Descodificador URL",
    uuidGenerator: "Gerador UUID",
  },
  th: {
    descriptionTemplate: "ใช้ {name} ออนไลน์ด้วยเวิร์กโฟลว์ที่รวดเร็วและปลอดภัย",
    converterTemplate: "ตัวแปลง {from} เป็น {to}",
    categories: { PDF: "PDF", Image: "รูปภาพ", Text: "ข้อความ", Dev: "Dev", Other: "อื่นๆ" },
    mergePdf: "รวมไฟล์ PDF",
    splitPdf: "แยกหน้า PDF",
    compressPdf: "บีบอัด PDF",
    rotatePdf: "หมุนหน้า PDF",
    addWatermark: "เพิ่มลายน้ำใน PDF",
    editPdf: "แก้ไข PDF ออนไลน์",
    signPdf: "เซ็น PDF แบบอิเล็กทรอนิกส์",
    imageResizer: "ปรับขนาดรูปภาพออนไลน์",
    imageCompressor: "บีบอัดรูปภาพออนไลน์",
    imageCropper: "ครอปรูปภาพออนไลน์",
    imageConverter: "ตัวแปลงรูปแบบรูปภาพ",
    removeBackground: "ลบพื้นหลังรูปภาพ",
    imageToVector: "รูปภาพเป็นเวกเตอร์ (SVG)",
    wordCounter: "ตัวนับคำ",
    caseConverter: "ตัวแปลงตัวพิมพ์",
    loremIpsum: "ตัวสร้าง Lorem Ipsum",
    jsonFormatter: "ตัวจัดรูปแบบ JSON",
    diffChecker: "ตัวตรวจสอบความแตกต่าง",
    markdownEditor: "ตัวแก้ไข Markdown",
    qrGenerator: "ตัวสร้าง QR Code",
    passwordGenerator: "ตัวสร้างรหัสผ่าน",
    base64Encoder: "ตัวเข้ารหัส Base64",
    base64Decoder: "ตัวถอดรหัส Base64",
    colorPicker: "ตัวเลือกสี",
    urlEncoder: "ตัวเข้ารหัส URL",
    urlDecoder: "ตัวถอดรหัส URL",
    uuidGenerator: "ตัวสร้าง UUID",
  },
  tr: {
    descriptionTemplate: "{name} aracını hızlı ve güvenli bir iş akışıyla çevrimiçi kullanın.",
    converterTemplate: "{from} - {to} Dönüştürücü",
    categories: { PDF: "PDF", Image: "Görsel", Text: "Metin", Dev: "Dev", Other: "Diğer" },
    mergePdf: "PDF dosyalarını birleştir",
    splitPdf: "PDF sayfalarını böl",
    compressPdf: "PDF sıkıştır",
    rotatePdf: "PDF sayfalarını döndür",
    addWatermark: "PDF'ye filigran ekle",
    editPdf: "PDF'yi çevrimiçi düzenle",
    signPdf: "PDF'yi elektronik olarak imzala",
    imageResizer: "Çevrimiçi görsel yeniden boyutlandırma",
    imageCompressor: "Çevrimiçi görsel sıkıştırma",
    imageCropper: "Görseli çevrimiçi kırp",
    imageConverter: "Görsel format dönüştürücü",
    removeBackground: "Görsel arka planını kaldır",
    imageToVector: "Görselden vektöre (SVG)",
    wordCounter: "Kelime sayacı",
    caseConverter: "Harf biçimi dönüştürücü",
    loremIpsum: "Lorem Ipsum oluşturucu",
    jsonFormatter: "JSON biçimleyici",
    diffChecker: "Fark denetleyici",
    markdownEditor: "Markdown düzenleyici",
    qrGenerator: "QR kod oluşturucu",
    passwordGenerator: "Şifre oluşturucu",
    base64Encoder: "Base64 kodlayıcı",
    base64Decoder: "Base64 çözücü",
    colorPicker: "Renk seçici",
    urlEncoder: "URL kodlayıcı",
    urlDecoder: "URL çözücü",
    uuidGenerator: "UUID oluşturucu",
  },
  el: {
    descriptionTemplate: "Χρησιμοποιήστε το {name} online με γρήγορο και ασφαλές workflow.",
    converterTemplate: "Μετατροπέας {from} σε {to}",
    categories: { PDF: "PDF", Image: "Εικόνα", Text: "Κείμενο", Dev: "Dev", Other: "Άλλα" },
    mergePdf: "Συγχώνευση αρχείων PDF",
    splitPdf: "Διαχωρισμός σελίδων PDF",
    compressPdf: "Συμπίεση PDF",
    rotatePdf: "Περιστροφή σελίδων PDF",
    addWatermark: "Προσθήκη υδατογραφήματος σε PDF",
    editPdf: "Επεξεργασία PDF online",
    signPdf: "Ηλεκτρονική υπογραφή PDF",
    imageResizer: "Αλλαγή μεγέθους εικόνας online",
    imageCompressor: "Συμπιεστής εικόνων online",
    imageCropper: "Περικοπή εικόνας online",
    imageConverter: "Μετατροπέας μορφής εικόνας",
    removeBackground: "Αφαίρεση φόντου εικόνας",
    imageToVector: "Εικόνα σε vector (SVG)",
    wordCounter: "Μετρητής λέξεων",
    caseConverter: "Μετατροπέας πεζών/κεφαλαίων",
    loremIpsum: "Γεννήτρια Lorem Ipsum",
    jsonFormatter: "Μορφοποιητής JSON",
    diffChecker: "Έλεγχος διαφορών",
    markdownEditor: "Επεξεργαστής Markdown",
    qrGenerator: "Γεννήτρια QR code",
    passwordGenerator: "Γεννήτρια κωδικών",
    base64Encoder: "Κωδικοποιητής Base64",
    base64Decoder: "Αποκωδικοποιητής Base64",
    colorPicker: "Επιλογέας χρώματος",
    urlEncoder: "Κωδικοποιητής URL",
    urlDecoder: "Αποκωδικοποιητής URL",
    uuidGenerator: "Γεννήτρια UUID",
  },
  uk: {
    descriptionTemplate: "Використовуйте {name} онлайн у швидкому та безпечному workflow.",
    converterTemplate: "Конвертер {from} у {to}",
    categories: { PDF: "PDF", Image: "Зображення", Text: "Текст", Dev: "Dev", Other: "Інше" },
    mergePdf: "Об'єднати PDF файли",
    splitPdf: "Розділити сторінки PDF",
    compressPdf: "Стиснути PDF",
    rotatePdf: "Повернути сторінки PDF",
    addWatermark: "Додати водяний знак до PDF",
    editPdf: "Редагувати PDF онлайн",
    signPdf: "Електронно підписати PDF",
    imageResizer: "Зміна розміру зображення онлайн",
    imageCompressor: "Стиснення зображень онлайн",
    imageCropper: "Обрізати зображення онлайн",
    imageConverter: "Конвертер форматів зображень",
    removeBackground: "Видалити фон із зображення",
    imageToVector: "Зображення у вектор (SVG)",
    wordCounter: "Лічильник слів",
    caseConverter: "Конвертер регістру",
    loremIpsum: "Генератор Lorem Ipsum",
    jsonFormatter: "Форматер JSON",
    diffChecker: "Порівняння відмінностей",
    markdownEditor: "Редактор Markdown",
    qrGenerator: "Генератор QR-кодів",
    passwordGenerator: "Генератор паролів",
    base64Encoder: "Кодувальник Base64",
    base64Decoder: "Декодувальник Base64",
    colorPicker: "Вибір кольору",
    urlEncoder: "Кодувальник URL",
    urlDecoder: "Декодувальник URL",
    uuidGenerator: "Генератор UUID",
  },
  ru: {
    descriptionTemplate: "Используйте {name} онлайн в быстром и безопасном workflow.",
    converterTemplate: "Конвертер {from} в {to}",
    categories: { PDF: "PDF", Image: "Изображение", Text: "Текст", Dev: "Dev", Other: "Другое" },
    mergePdf: "Объединить PDF файлы",
    splitPdf: "Разделить страницы PDF",
    compressPdf: "Сжать PDF",
    rotatePdf: "Повернуть страницы PDF",
    addWatermark: "Добавить водяной знак в PDF",
    editPdf: "Редактировать PDF онлайн",
    signPdf: "Подписать PDF электронно",
    imageResizer: "Изменить размер изображения онлайн",
    imageCompressor: "Сжать изображение онлайн",
    imageCropper: "Обрезать изображение онлайн",
    imageConverter: "Конвертер форматов изображений",
    removeBackground: "Удалить фон изображения",
    imageToVector: "Изображение в вектор (SVG)",
    wordCounter: "Счётчик слов",
    caseConverter: "Конвертер регистра",
    loremIpsum: "Генератор Lorem Ipsum",
    jsonFormatter: "Форматтер JSON",
    diffChecker: "Проверка различий",
    markdownEditor: "Редактор Markdown",
    qrGenerator: "Генератор QR-кодов",
    passwordGenerator: "Генератор паролей",
    base64Encoder: "Кодировщик Base64",
    base64Decoder: "Декодировщик Base64",
    colorPicker: "Палитра цветов",
    urlEncoder: "Кодировщик URL",
    urlDecoder: "Декодировщик URL",
    uuidGenerator: "Генератор UUID",
  },
  ar: {
    descriptionTemplate: "استخدم {name} عبر الإنترنت ضمن سير عمل سريع وآمن.",
    converterTemplate: "محول {from} إلى {to}",
    categories: { PDF: "PDF", Image: "صور", Text: "نص", Dev: "Dev", Other: "أخرى" },
    mergePdf: "دمج ملفات PDF",
    splitPdf: "تقسيم صفحات PDF",
    compressPdf: "ضغط ملف PDF",
    rotatePdf: "تدوير صفحات PDF",
    addWatermark: "إضافة علامة مائية إلى PDF",
    editPdf: "تحرير PDF عبر الإنترنت",
    signPdf: "توقيع PDF إلكترونيًا",
    imageResizer: "تغيير حجم الصور عبر الإنترنت",
    imageCompressor: "ضغط الصور عبر الإنترنت",
    imageCropper: "اقتصاص الصورة عبر الإنترنت",
    imageConverter: "محول صيغ الصور",
    removeBackground: "إزالة خلفية الصورة",
    imageToVector: "تحويل الصورة إلى vector (SVG)",
    wordCounter: "عداد الكلمات",
    caseConverter: "محول حالة الأحرف",
    loremIpsum: "مولد Lorem Ipsum",
    jsonFormatter: "منسق JSON",
    diffChecker: "مدقق الفروقات",
    markdownEditor: "محرر Markdown",
    qrGenerator: "مولد رموز QR",
    passwordGenerator: "مولد كلمات المرور",
    base64Encoder: "مشفر Base64",
    base64Decoder: "مفكك Base64",
    colorPicker: "منتقي الألوان",
    urlEncoder: "مشفر URL",
    urlDecoder: "مفكك URL",
    uuidGenerator: "مولد UUID",
  },
  ko: {
    descriptionTemplate: "{name} 도구를 빠르고 안전한 워크플로우로 온라인에서 사용하세요.",
    converterTemplate: "{from} → {to} 변환기",
    categories: { PDF: "PDF", Image: "이미지", Text: "텍스트", Dev: "개발", Other: "기타" },
    mergePdf: "PDF 파일 합치기",
    splitPdf: "PDF 페이지 분리",
    compressPdf: "PDF 압축",
    rotatePdf: "PDF 페이지 회전",
    addWatermark: "PDF에 워터마크 추가",
    editPdf: "온라인 PDF 편집",
    signPdf: "전자 서명 PDF",
    imageResizer: "이미지 크기 조절",
    imageCompressor: "이미지 압축",
    imageCropper: "이미지 자르기",
    imageConverter: "이미지 형식 변환기",
    removeBackground: "이미지 배경 제거",
    imageToVector: "이미지를 벡터로 (SVG)",
    wordCounter: "단어 수 세기",
    caseConverter: "대소문자 변환기",
    loremIpsum: "Lorem Ipsum 생성기",
    jsonFormatter: "JSON 포맷터",
    diffChecker: "차이 비교기",
    markdownEditor: "Markdown 편집기",
    qrGenerator: "QR 코드 생성기",
    passwordGenerator: "비밀번호 생성기",
    base64Encoder: "Base64 인코더",
    base64Decoder: "Base64 디코더",
    colorPicker: "색상 선택기",
    urlEncoder: "URL 인코더",
    urlDecoder: "URL 디코더",
    uuidGenerator: "UUID 생성기",
  },
  ja: {
    descriptionTemplate: "{name} を高速で安全なワークフローでオンライン利用できます。",
    converterTemplate: "{from} から {to} への変換ツール",
    categories: { PDF: "PDF", Image: "画像", Text: "テキスト", Dev: "開発", Other: "その他" },
    mergePdf: "PDFファイルを結合",
    splitPdf: "PDFページを分割",
    compressPdf: "PDFを圧縮",
    rotatePdf: "PDFページを回転",
    addWatermark: "PDFに透かしを追加",
    editPdf: "PDFをオンライン編集",
    signPdf: "PDFに電子署名",
    imageResizer: "画像リサイズ",
    imageCompressor: "画像圧縮",
    imageCropper: "画像をオンライン切り抜き",
    imageConverter: "画像形式変換ツール",
    removeBackground: "画像の背景を削除",
    imageToVector: "画像をベクター化 (SVG)",
    wordCounter: "文字・単語カウンター",
    caseConverter: "大文字小文字変換",
    loremIpsum: "Lorem Ipsum 生成",
    jsonFormatter: "JSON フォーマッター",
    diffChecker: "差分チェッカー",
    markdownEditor: "Markdown エディター",
    qrGenerator: "QRコード生成",
    passwordGenerator: "パスワード生成",
    base64Encoder: "Base64 エンコーダー",
    base64Decoder: "Base64 デコーダー",
    colorPicker: "カラーピッカー",
    urlEncoder: "URL エンコーダー",
    urlDecoder: "URL デコーダー",
    uuidGenerator: "UUID 生成",
  },
  ro: {
    descriptionTemplate: "Folosește {name} online cu un flux rapid și sigur.",
    converterTemplate: "Convertor {from} în {to}",
    categories: { PDF: "PDF", Image: "Imagine", Text: "Text", Dev: "Dev", Other: "Altele" },
    mergePdf: "Îmbină fișiere PDF",
    splitPdf: "Separă pagini PDF",
    compressPdf: "Comprimă PDF",
    rotatePdf: "Rotește pagini PDF",
    addWatermark: "Adaugă watermark în PDF",
    editPdf: "Editează PDF online",
    signPdf: "Semnează PDF electronic",
    imageResizer: "Redimensionare imagini online",
    imageCompressor: "Compresor imagini online",
    imageCropper: "Decupează imagine online",
    imageConverter: "Convertor formate imagine",
    removeBackground: "Elimină fundalul imaginii",
    imageToVector: "Imagine în vector (SVG)",
    wordCounter: "Contor de cuvinte",
    caseConverter: "Convertor de litere",
    loremIpsum: "Generator Lorem Ipsum",
    jsonFormatter: "Formatter JSON",
    diffChecker: "Verificator diferențe",
    markdownEditor: "Editor Markdown",
    qrGenerator: "Generator coduri QR",
    passwordGenerator: "Generator parole",
    base64Encoder: "Codificator Base64",
    base64Decoder: "Decodificator Base64",
    colorPicker: "Selector de culori",
    urlEncoder: "Codificator URL",
    urlDecoder: "Decodificator URL",
    uuidGenerator: "Generator UUID",
  },
};

const converterPairs: Record<string, [string, string]> = {
  "pdf-to-jpg": ["PDF", "JPG"],
  "pdf-to-png": ["PDF", "PNG"],
  "jpg-to-pdf": ["JPG", "PDF"],
  "word-to-pdf": ["Word", "PDF"],
  "pdf-to-word": ["PDF", "Word"],
  "pdf-to-excel": ["PDF", "Excel"],
  "excel-to-pdf": ["Excel", "PDF"],
  "pdf-to-powerpoint": ["PDF", "PowerPoint"],
  "jpg-to-png": ["JPG", "PNG"],
  "png-to-jpg": ["PNG", "JPG"],
  "webp-to-jpg": ["WebP", "JPG"],
  "webp-to-png": ["WebP", "PNG"],
  "png-to-webp": ["PNG", "WebP"],
  "jpg-to-webp": ["JPG", "WebP"],
};

function fillTemplate(template: string, values: Record<string, string>) {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, value),
    template
  );
}

function getLocalizedToolName(tool: Tool, locale: Locale) {
  const pack = toolLocalePacks[locale] ?? toolLocalePacks.en;
  const pair = converterPairs[tool.id];

  if (pair) {
    return fillTemplate(pack.converterTemplate, { from: pair[0], to: pair[1] });
  }

  switch (tool.id) {
    case "merge-pdf":
      return pack.mergePdf;
    case "split-pdf":
      return pack.splitPdf;
    case "compress-pdf":
      return pack.compressPdf;
    case "rotate-pdf":
      return pack.rotatePdf;
    case "add-watermark":
      return pack.addWatermark;
    case "edit-pdf":
      return pack.editPdf;
    case "sign-pdf":
      return pack.signPdf;
    case "image-resizer":
      return pack.imageResizer;
    case "image-compressor":
      return pack.imageCompressor;
    case "image-cropper":
      return pack.imageCropper;
    case "image-converter":
      return pack.imageConverter;
    case "remove-background":
      return pack.removeBackground;
    case "image-to-vector":
      return pack.imageToVector;
    case "word-counter":
      return pack.wordCounter;
    case "case-converter":
      return pack.caseConverter;
    case "lorem-ipsum":
      return pack.loremIpsum;
    case "json-formatter":
      return pack.jsonFormatter;
    case "diff-checker":
      return pack.diffChecker;
    case "markdown-editor":
      return pack.markdownEditor;
    case "qr-generator":
      return pack.qrGenerator;
    case "password-generator":
      return pack.passwordGenerator;
    case "base64-encode":
      return pack.base64Encoder;
    case "base64-decode":
      return pack.base64Decoder;
    case "color-picker":
      return pack.colorPicker;
    case "url-encoder":
      return pack.urlEncoder;
    case "url-decoder":
      return pack.urlDecoder;
    case "uuid-generator":
      return pack.uuidGenerator;
    default:
      return tool.name;
  }
}

export function getLocalizedTool(tool: Tool, locale: Locale): Tool {
  const pack = toolLocalePacks[locale] ?? toolLocalePacks.en;
  const name = getLocalizedToolName(tool, locale);

  return {
    ...tool,
    name,
    description: fillTemplate(pack.descriptionTemplate, { name }),
    category: pack.categories[tool.category] ?? tool.category,
  };
}

export function getLocalizedTools<T extends Tool>(items: T[], locale: Locale): T[] {
  return items.map((tool) => getLocalizedTool(tool, locale) as T);
}
